import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppShell from '../../../components/layout/AppShell';
import TechDaySelector, {
  addDays,
  dayKey,
  sameDay,
  startOfDay
} from '../../../components/technician/TechDaySelector';
import '../../admin/quotes/quote-generator.css';
import { apiRequest } from '../../../api/client';
import { formatClockLocationLabel } from '../../../utils/clockLocationDisplay';
import '../technician.css';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type ClockLocation = {
  lat?: number;
  lng?: number;
  accuracy?: number;
  label?: string;
};

type TimeEntry = {
  _id: string;
  date: string;
  hours: number;
  siteName?: string;
  workOrderTitle?: string;
  status?: 'submitted' | 'approved' | 'rejected';
  kind?: 'manual' | 'clock';
  clockInAt?: string | null;
  clockOutAt?: string | null;
  clockInLocation?: ClockLocation | null;
  clockOutLocation?: ClockLocation | null;
  notes?: string;
};

/** Reads geolocation. Resolves with `null` if denied/unsupported/timed-out (never throws). */
function tryGetLocation(): Promise<ClockLocation | null> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !('geolocation' in navigator)) {
      resolve(null);
      return;
    }
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30_000 }
      );
    } catch {
      resolve(null);
    }
  });
}

/** Format a millisecond duration as HH:MM:SS for the live elapsed display. */
function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) ms = 0;
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatTime(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatLocation(loc?: ClockLocation | null, siteFallback?: string): string {
  const place = formatClockLocationLabel(loc, '');
  if (place) return place;
  if (siteFallback?.trim()) return siteFallback.trim();
  return 'Area unknown';
}

function TimeSheet(): React.JSX.Element {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [active, setActive] = useState<TimeEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [now, setNow] = useState<number>(Date.now());
  const today = useMemo(() => startOfDay(new Date()), []);
  const [selectedDay, setSelectedDay] = useState<Date>(today);

  // Manual time entry (parity with mobile)
  const [showManual, setShowManual] = useState(false);
  const [manualDate, setManualDate] = useState('');
  const [manualHours, setManualHours] = useState('');
  const [manualSite, setManualSite] = useState('');
  const [manualNotes, setManualNotes] = useState('');
  const [savingManual, setSavingManual] = useState(false);

  /** Used to clear any pending info banner timer when the component re-renders. */
  const infoTimerRef = useRef<number | null>(null);

  const showInfo = useCallback((msg: string, ms = 4000) => {
    setInfo(msg);
    if (infoTimerRef.current) window.clearTimeout(infoTimerRef.current);
    infoTimerRef.current = window.setTimeout(() => setInfo(null), ms);
  }, []);

  const refreshAll = useCallback(async () => {
    try {
      const [list, current] = await Promise.all([
        apiRequest<{ data: TimeEntry[] }>('/api/time-entries'),
        apiRequest<{ data: TimeEntry | null }>('/api/time-entries/active')
      ]);
      setEntries(list.data || []);
      setActive(current.data || null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load time entries.');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    refreshAll().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
      if (infoTimerRef.current) window.clearTimeout(infoTimerRef.current);
    };
  }, [refreshAll]);

  /** Tick a 1s timer only while there's an active clock entry (drives the live HH:MM:SS readout). */
  useEffect(() => {
    if (!active?.clockInAt) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active?.clockInAt]);

  const elapsedMs = useMemo(() => {
    if (!active?.clockInAt) return 0;
    const start = new Date(active.clockInAt).getTime();
    return Math.max(0, now - start);
  }, [active, now]);

  const elapsedHoursFloat = elapsedMs / 3_600_000;

  async function handleClockIn() {
    if (punching) return;
    setError(null);
    setPunching(true);
    try {
      const location = await tryGetLocation();
      const res = await apiRequest<{ data: TimeEntry }>('/api/time-entries/clock-in', {
        method: 'POST',
        json: { location }
      });
      // Defensive: if the server reply is missing clockInAt for any reason, fall back to
      // "right now" so the UI still flips to the on-the-clock state.
      const entry: TimeEntry = res.data?.clockInAt
        ? res.data
        : { ...(res.data || ({} as TimeEntry)), clockInAt: new Date().toISOString() };
      setActive(entry);
      showInfo(
        location
          ? `Clocked in at ${formatTime(entry.clockInAt)} · location captured`
          : `Clocked in at ${formatTime(entry.clockInAt)} · location unavailable`
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to clock in.');
    } finally {
      setPunching(false);
    }
  }

  async function handleClockOut() {
    if (punching) return;
    setError(null);
    setPunching(true);
    try {
      const location = await tryGetLocation();
      const res = await apiRequest<{ data: TimeEntry }>('/api/time-entries/clock-out', {
        method: 'POST',
        json: { location }
      });
      setActive(null);
      showInfo(
        `Clocked out at ${formatTime(res.data.clockOutAt)} · ${res.data.hours.toFixed(2)} h logged`
      );
      await refreshAll();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to clock out.');
    } finally {
      setPunching(false);
    }
  }

  async function addManualEntry() {
    if (savingManual) return;
    if (!manualDate || !manualHours) {
      setError('Date and hours are required.');
      return;
    }
    const h = Number(manualHours);
    if (Number.isNaN(h) || h < 0) {
      setError('Enter a valid number of hours.');
      return;
    }
    setError(null);
    setSavingManual(true);
    try {
      await apiRequest('/api/time-entries', {
        method: 'POST',
        json: {
          workOrderTitle: manualSite || 'General',
          siteName: manualSite || '—',
          siteAddress: '',
          date: manualDate,
          hours: h,
          notes: manualNotes || undefined
        }
      });
      setManualDate('');
      setManualHours('');
      setManualSite('');
      setManualNotes('');
      setShowManual(false);
      await refreshAll();
      showInfo('Manual entry added.');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not save entry.');
    } finally {
      setSavingManual(false);
    }
  }

  const statusClass = (status?: string) => {
    if (status === 'approved') return 'ok';
    if (status === 'rejected') return 'warn';
    return 'warn';
  };

  const weekTotal = useMemo(() => {
    return entries.reduce((sum, e) => sum + (Number(e.hours) || 0), 0);
  }, [entries]);

  /** Map of dayKey -> entries logged that day. */
  const entriesByDay = useMemo(() => {
    const map = new Map<string, TimeEntry[]>();
    for (const entry of entries) {
      if (!entry.date) continue;
      const d = new Date(entry.date);
      if (Number.isNaN(d.getTime())) continue;
      const key = dayKey(startOfDay(d));
      const list = map.get(key) || [];
      list.push(entry);
      map.set(key, list);
    }
    return map;
  }, [entries]);

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    entriesByDay.forEach((list, key) => m.set(key, list.length));
    return m;
  }, [entriesByDay]);

  const selectedEntries = entriesByDay.get(dayKey(selectedDay)) || [];
  const dayTotal = selectedEntries.reduce((sum, e) => sum + (Number(e.hours) || 0), 0);

  const selectedHeading = `${DOW[selectedDay.getDay()]}, ${MONTHS[selectedDay.getMonth()]} ${selectedDay.getDate()}`;
  const relLabel = sameDay(selectedDay, today)
    ? 'Today'
    : sameDay(selectedDay, addDays(today, 1))
      ? 'Tomorrow'
      : sameDay(selectedDay, addDays(today, -1))
        ? 'Yesterday'
        : selectedDay.getTime() < today.getTime()
          ? 'Past'
          : 'Upcoming';

  const isActive = Boolean(active?.clockInAt);

  return (
    <AppShell>
      <div className="tech-page">
        <header className="tech-hero">
          <div>
            <p className="tech-kicker">Hours Tracking</p>
            <h1 className="tech-title">Time Sheet</h1>
          </div>
          <div className="tech-actions">
            <div className="tech-week-total" aria-label="Hours logged this period">
              <span className="tech-week-total-label">Logged</span>
              <span className="tech-week-total-value">{weekTotal.toFixed(2)} h</span>
            </div>
          </div>
        </header>

        {error ? (
          <div className="tech-banner tech-banner-error" role="alert">
            {error}
            <button
              type="button"
              className="tech-banner-close"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        ) : null}

        {info ? <div className="tech-banner tech-banner-info" role="status">{info}</div> : null}

        {/* ─────────────────────────── Clock-in / Clock-out card ─────────────────────────── */}
        <div className={`tech-clock-card ${isActive ? 'is-active' : ''}`}>
          <div className="tech-clock-status">
            <span className={`tech-clock-dot ${isActive ? 'is-on' : ''}`} aria-hidden="true" />
            <span className="tech-clock-status-text">
              {loading ? 'Loading…' : isActive ? 'On the clock' : 'Off the clock'}
            </span>
          </div>

          {isActive ? (
            <>
              <div className="tech-clock-elapsed" aria-live="polite">
                <span className="tech-clock-elapsed-value">{formatElapsed(elapsedMs)}</span>
                <span className="tech-clock-elapsed-hint">
                  {elapsedHoursFloat.toFixed(2)} h elapsed
                </span>
              </div>
              <dl className="tech-clock-meta">
                <div>
                  <dt>Clocked in</dt>
                  <dd>{formatTime(active?.clockInAt)}</dd>
                </div>
                <div>
                  <dt>Location</dt>
                  <dd title={formatLocation(active?.clockInLocation, active?.siteName || active?.workOrderTitle)}>
                    {formatLocation(active?.clockInLocation, active?.siteName || active?.workOrderTitle)}
                  </dd>
                </div>
              </dl>
              <button
                type="button"
                className="tech-clock-btn tech-clock-btn-out"
                onClick={handleClockOut}
                disabled={punching}
              >
                {punching ? 'Clocking out…' : 'Clock Out'}
              </button>
              <p className="tech-clock-hint">
                Hours will be calculated automatically when you clock out.
              </p>
            </>
          ) : (
            <>
              <div className="tech-clock-elapsed tech-clock-elapsed--idle">
                <span className="tech-clock-elapsed-value">00:00:00</span>
                <span className="tech-clock-elapsed-hint">Press Clock In to start a shift</span>
              </div>
              <button
                type="button"
                className="tech-clock-btn tech-clock-btn-in"
                onClick={handleClockIn}
                disabled={loading || punching}
              >
                {punching ? 'Clocking in…' : 'Clock In'}
              </button>
              <p className="tech-clock-hint">
                We'll record the time and (if allowed) your location at clock-in and clock-out.
              </p>
            </>
          )}
        </div>

        {/* ─────────────────────────── Manual entry ─────────────────────────── */}
        <button
          type="button"
          className="tech-manual-toggle"
          onClick={() => setShowManual((v) => !v)}
        >
          {showManual ? 'Hide manual entry –' : 'Add manual entry +'}
        </button>

        {showManual ? (
          <div className="tech-card tech-manual-card">
            <div className="tech-manual-row">
              <label className="tech-manual-field">
                <span className="tech-manual-label">Date</span>
                <input
                  type="date"
                  className="form-input"
                  value={manualDate}
                  onChange={(e) => setManualDate(e.target.value)}
                />
              </label>
              <label className="tech-manual-field">
                <span className="tech-manual-label">Hours</span>
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  className="form-input"
                  placeholder="0.00"
                  value={manualHours}
                  onChange={(e) => setManualHours(e.target.value)}
                />
              </label>
            </div>
            <label className="tech-manual-field">
              <span className="tech-manual-label">Job / Site (optional)</span>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. 123 Main St inspection"
                value={manualSite}
                onChange={(e) => setManualSite(e.target.value)}
              />
            </label>
            <label className="tech-manual-field">
              <span className="tech-manual-label">Notes (optional)</span>
              <textarea
                className="form-input"
                rows={3}
                value={manualNotes}
                onChange={(e) => setManualNotes(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="tech-btn tech-btn-primary"
              onClick={addManualEntry}
              disabled={savingManual}
            >
              {savingManual ? 'Saving…' : 'Add Entry'}
            </button>
          </div>
        ) : null}

        {/* ─────────────────────────── Day selector ─────────────────────────── */}
        <TechDaySelector selectedDay={selectedDay} onSelectDay={setSelectedDay} counts={counts} />

        <div className="tech-day-header">
          <div>
            <span className="tech-day-header__rel">{relLabel}</span>
            <h2 className="tech-day-header__title">{selectedHeading}</h2>
          </div>
          <span className="tech-day-header__count">{dayTotal.toFixed(2)} h</span>
        </div>

        {/* ─────────────────────────── Entries list ─────────────────────────── */}
        <div className="tech-card" style={{ padding: 0, overflow: 'hidden' }}>
          {selectedEntries.length === 0 ? (
            <div className="tech-empty" style={{ padding: '24px 20px 28px' }}>
              <p style={{ margin: 0, color: 'var(--tech-muted)' }}>
                No time entries for this day. Use <strong>Clock In</strong> above or pick another day.
              </p>
            </div>
          ) : (
            <div className="list-responsive" style={{ marginTop: 12 }}>
              <div className="list-table-pane">
                <table className="tech-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Clock in</th>
                      <th>Clock out</th>
                      <th>Hours</th>
                      <th>Site / job</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedEntries.map((entry) => {
                      const isOpen = entry.kind === 'clock' && !entry.clockOutAt;
                      return (
                        <tr key={entry._id}>
                          <td>{entry.date ? new Date(entry.date).toLocaleDateString() : '—'}</td>
                          <td>{formatTime(entry.clockInAt)}</td>
                          <td>{isOpen ? <em>open</em> : formatTime(entry.clockOutAt)}</td>
                          <td>{isOpen ? '—' : Number(entry.hours || 0).toFixed(2)}</td>
                          <td>{entry.siteName || entry.workOrderTitle || '—'}</td>
                          <td>
                            <div className="tech-entry-status">
                              <span className={`tech-badge ${statusClass(entry.status)}`}>
                                {isOpen ? 'in progress' : entry.status || 'submitted'}
                              </span>
                              {entry.kind === 'manual' ? (
                                <span className="tech-manual-tag">Added manually</span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="list-cards-pane">
                {selectedEntries.map((entry) => {
                  const isOpen = entry.kind === 'clock' && !entry.clockOutAt;
                  return (
                    <article key={`m-${entry._id}`} className="list-land-card">
                      <div className="list-land-field">
                        <span className="list-land-label">Date</span>
                        <span className="list-land-value">
                          {entry.date ? new Date(entry.date).toLocaleDateString() : '—'}
                        </span>
                      </div>
                      <div className="list-land-field">
                        <span className="list-land-label">Hours</span>
                        <span className="list-land-value">
                          {isOpen ? '—' : Number(entry.hours || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="list-land-field">
                        <span className="list-land-label">Clock in</span>
                        <span className="list-land-value">{formatTime(entry.clockInAt)}</span>
                      </div>
                      <div className="list-land-field">
                        <span className="list-land-label">Clock out</span>
                        <span className="list-land-value">
                          {isOpen ? <em>open</em> : formatTime(entry.clockOutAt)}
                        </span>
                      </div>
                      <div className="list-land-field list-land-field--span">
                        <span className="list-land-label">Site / job</span>
                        <span className="list-land-value">
                          {entry.siteName || entry.workOrderTitle || '—'}
                        </span>
                      </div>
                      <div className="list-land-field">
                        <span className="list-land-label">Status</span>
                        <span className="list-land-value tech-entry-status">
                          <span className={`tech-badge ${statusClass(entry.status)}`}>
                            {isOpen ? 'in progress' : entry.status || 'submitted'}
                          </span>
                          {entry.kind === 'manual' ? (
                            <span className="tech-manual-tag">Added manually</span>
                          ) : null}
                        </span>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

export default TimeSheet;
